import { useCallback, useEffect, useRef, useState } from 'react'
import { QMark } from '../../components/Wordmark'
import GrainField from '../../components/GrainField'
import { Label } from '../../components/Label'
import Rail from '../../components/kit/Rail'
import Counted from '../../components/kit/Counted'
import ExpandCard from '../../components/kit/ExpandCard'
import { useReveal } from '../../components/kit/useReveal'
import WritingDemo from './WritingDemo'
import styles from './Landing.module.css'

/**
 * QUIL — the public landing page, and the app's only front door.
 *
 * P4 of the portfolio push, built under `app-platform/design/LANDING-PAGE-SPEC.md`, which is a
 * *method* rather than a template.
 *
 * ── The one true picture (spec §2) ───────────────────────────────────────────────────────────────
 * **The page being written on, with the line underneath it that knows where you are.** Not the
 * library and not the plan: the Writing Surface itself. `WritingDemo.tsx` renders that surface for
 * real — the app's own chrome, its own cream page, its own chapter scale — and types a paragraph
 * into it while the proofing checks underline their three real findings as they land. It would be
 * meaningless on a recipe app, a feed, a time tracker or a branching-story editor, which is the
 * test the spec sets.
 *
 * ── What this page is NOT allowed to say ─────────────────────────────────────────────────────────
 * **Publicly, Quil is a writing and story-planning app.** Kamrin, 2026-09-23. Not fan fiction, not
 * a fandom tool, and not named as one anywhere on this page — the story shapes are craft
 * structures, the trope frameworks are romance and genre patterns, and the export is described by
 * what it produces rather than where it goes. Everything here is still true of the app; the framing
 * is what changed. A later session tempted to "restore the fanfic angle" should read this and stop.
 *
 * ── The modules (spec §5) ────────────────────────────────────────────────────────────────────────
 * From the twelve the P3 review kept: **02** hero, **05** statement and counted stats, **07** the
 * manual captioned rail, **10** the expanding feature grid, **14** footer, plus **01**, the sticky
 * local nav — which the review CUT, and which Kamrin asked for by name on 2026-09-23 ("when i
 * scroll down a locked nav bar at the top should show"). He cut it; he can uncut it. Recorded so
 * the contradiction with REVIEW.md reads as a decision rather than as drift.
 *
 * **Module 06, the ground swatches, is GONE.** It shipped on 2026-09-22 and Kamrin's read was that
 * a theme picker is not a reason to use the app: *"instead you talk about the reasoning behind the
 * app."* Its slot is now "Why this exists", which is the argument the page was missing. The live
 * `resolveGround` demo was the most technically impressive thing here and the least persuasive, and
 * that is worth remembering rather than re-deriving.
 *
 * **The refusals ledger is also GONE**, for the same kind of reason: it earns its place on
 * Contently, where refusing the social-media pattern IS the product. Quil is not fighting anything,
 * so a list of what it will never do read as borrowed. 09, 03, 04, 11 and 13 remain out; §4 of the
 * design contract says why for each.
 *
 * ── Grounds ──────────────────────────────────────────────────────────────────────────────────────
 * The page alternates its ground deliberately (Kamrin, 2026-09-23: sections blended into one
 * another). Forest for the hero and the rail, the **cream page** for "Why this exists" — the same
 * cream the writing surface uses, so the argument about writing is set on the thing you write on —
 * and the deeper `--sunk` for the two data-heavy bands.
 */

/** Terminal stop on a Jagot heading, lit. DESIGN.md §3 "the full stop". */
function Stop() {
  return <span className={styles.stop}>.</span>
}

const SECTIONS = [
  { id: 'why', label: 'Why' },
  { id: 'shape', label: 'The shape' },
  { id: 'surfaces', label: 'Surfaces' },
  { id: 'detail', label: 'Detail' },
  { id: 'stack', label: 'Under the hood' },
]

/** Seven surfaces, one draft. */
const SURFACES: { kicker: string; title: string; body: string }[] = [
  {
    kicker: 'Plan',
    title: 'One guided pass, not a blank canvas',
    body:
      'The wizard asks for the shape, the cast, the relationships, the chapters and the scenes, in that order, and you can answer as little as you like. There is no separate planning document to keep in step with the draft; there was once, and it was retired because two places describing one story is one too many.',
  },
  {
    kicker: 'Write',
    title: 'A cream page inside a dark room',
    body:
      'The chapter is one continuous document on a real page with its own ink. Click the paper and the chrome slides out of the way. Press Escape and it comes back.',
  },
  {
    kicker: 'Navigate',
    title: 'Scenes are anchors, not breaks',
    body:
      'A scene drops a faint hairline into the prose where it begins, and the navigator jumps to it. Your chapter never acquires a row of asterisks it did not ask for.',
  },
  {
    kicker: 'Mark',
    title: 'Four colours that mean what you decide',
    body:
      'Rewrite, finish, delete, check. They underline rather than fill, so the prose underneath stays the only thing at full contrast, and nothing tells you what they are for.',
  },
  {
    kicker: 'Proof',
    title: '15 checks, and an off switch on each',
    body:
      'Weasel words, adverbs on dialogue tags, clichés, passive voice, pacing by dialogue ratio, name consistency, weak chapter openings. Rules, not a model. A check you disagree with goes off and stays off.',
  },
  {
    kicker: 'Finish',
    title: 'Export that is already formatted',
    body:
      'Tags, content warnings, cast, relationships, summary, notes and a preview, stacked on one page. The cast section writes back to your real characters rather than to an export-only copy.',
  },
  {
    kicker: 'Keep',
    title: 'Files you can open without Quil',
    body:
      'Everything lands in your own OneDrive as ordinary files. If this app disappeared tomorrow the work would still be there, and still openable.',
  },
]

/** The portfolio section. Every line is checkable against the repo. */
const STACK: { term: string; body: string }[] = [
  {
    term: 'Front end',
    body:
      'React 19, Vite and TypeScript in strict mode. No component library and no state library: every surface is built from a written design contract, and a build script fails if a rule in it is broken.',
  },
  {
    term: 'Storage',
    body:
      'Microsoft Graph writes straight into your own OneDrive, which is the source of truth. IndexedDB holds the working copy, so a keystroke is saved locally before anything touches a network.',
  },
  {
    term: 'Sync',
    body:
      'Prose syncs per file with cTag conflict detection; planning data merges last-write-wins with tombstones. Two devices that both moved on get asked which to keep, never silently overwritten.',
  },
  {
    term: 'Editor',
    body:
      'TipTap, loaded only when a chapter opens. Prose is stored as JSON and converted to clean, publish-safe HTML at export, so the editor never has to think about someone else’s markup.',
  },
  {
    term: 'Install',
    body:
      'A progressive web app on Cloudflare Pages. Add it to a home screen and it works offline. There is no store, no install step, and no server of mine between you and your files.',
  },
  {
    term: 'Testing',
    body:
      'Over 2,000 unit tests and an end-to-end browser suite on every change, plus a contrast gate of a hundred colour pairings and an accessibility scan that has to come back clean.',
  },
]

export default function LandingPage({
  onSignIn,
  canWorkOffline,
  onWorkOffline,
}: {
  onSignIn: () => Promise<void> | void
  canWorkOffline?: boolean
  onWorkOffline?: () => void
}) {
  const rootRef = useRef<HTMLElement>(null)
  const heroRef = useRef<HTMLDivElement>(null)
  useReveal(rootRef, styles.armed ?? 'armed', styles.allIn ?? 'allIn')

  const [pending, setPending] = useState(false)
  const [navOn, setNavOn] = useState(false)

  /**
   * Module 01: the bar locks to the top once the hero has gone past. Driven by a scroll listener
   * rather than an observer, for the same reason the reveal keeps several signals: the nav failing
   * open (always visible) would be ugly, but failing shut means a page with no way back to the top.
   * A scroll handler cannot silently never fire.
   */
  useEffect(() => {
    const hero = heroRef.current
    if (!hero) return
    let raf = 0
    const read = () => {
      raf = 0
      setNavOn(hero.getBoundingClientRect().bottom < 72)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read)
    }
    read()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  const signIn = useCallback(async () => {
    setPending(true)
    try {
      await onSignIn()
    } catch {
      // The popup was closed, or the redirect could not start. Let them try again.
      setPending(false)
    }
  }, [onSignIn])

  const cta = (variant: 'primary' | 'quiet' | 'bar', testid?: string) => (
    <button
      type="button"
      className={`${styles.btn} ${
        variant === 'primary' ? styles.btnPrimary : variant === 'bar' ? styles.btnBar : styles.btnQuiet
      }`}
      onClick={() => void signIn()}
      disabled={pending}
      data-testid={testid}
    >
      {pending ? 'Signing in' : 'Sign in with Microsoft'}
    </button>
  )

  return (
    // id/tabIndex keep the shell's skip-link target (#main) alive on this screen, exactly as the
    // sign-in page it replaces did. data-theme is set on this element rather than on <html>: the
    // token blocks are plain attribute selectors, so the whole subtree resolves dark regardless of
    // what the signed-in app's personalisation later writes to the document root.
    <main id="main" tabIndex={-1} ref={rootRef} data-theme="dark" className={styles.landing}>
      <GrainField />

      {/* ═══ 01 · STICKY LOCAL NAV ══════════════════════════════════════════════════════
          `inert` as well as hidden: an off-screen bar that is still focusable is a tab stop
          leading nowhere, which is the trap Contently's own header fell into. */}
      <div className={styles.navbar} data-on={navOn} inert={!navOn}>
        <div className={styles.navInner}>
          <a className={styles.navMark} href="#main" aria-label="Quil, back to top">
            <QMark />
          </a>
          <nav className={styles.navLinks} aria-label="Sections">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`}>
                {s.label}
              </a>
            ))}
          </nav>
          {cta('bar')}
        </div>
      </div>

      {/* ═══ 02 · HERO ══════════════════════════════════════════════════════════════════ */}
      <section className={`${styles.wrap} ${styles.hero}`} ref={heroRef}>
        {/* The mark, a rule, then the heading — so the identity introduces the sentence instead of
            competing with it at a tenth of its size (Kamrin, 2026-09-23). */}
        <div className={styles.lockup}>
          <QMark className={styles.heroMark} />
          <span className={styles.lockupRule} aria-hidden="true" />
        </div>
        <h1 className={styles.heroTitle}>
          The draft keeps its shape
          <Stop />
        </h1>
        <p className={styles.lede}>
          A place to plan a story and write it, in one piece. The plan sits under the page rather
          than in another document, so the draft always knows where it is.
        </p>
        <div className={styles.ctas}>
          {cta('primary', 'signin-button')}
          {/* The captive-portal escape (D8), rehomed from the sign-in screen this page replaced.
              A dead network that still reports `onLine === true` would otherwise strand a device
              away from a library it already holds locally, so it belongs at the top where someone
              stuck can reach it without reading a page first. */}
          {canWorkOffline && (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={() => onWorkOffline?.()}
              data-testid="work-offline-button"
            >
              Keep writing offline
            </button>
          )}
        </div>
        <p className={styles.note}>
          {canWorkOffline
            ? 'Your work stays in your own OneDrive. Sync resumes next time you sign in.'
            : 'Your work stays in your own OneDrive.'}
        </p>

        <WritingDemo />
      </section>

      {/* ═══ WHY THIS EXISTS · the cream page, because the argument is about writing ════ */}
      <section id="why" className={`${styles.section} ${styles.cream}`}>
        <div className={styles.wrap}>
          <h2 className={styles.title} data-reveal>
            Why this exists
            <Stop />
          </h2>
          <div className={styles.creamBody}>
            <p data-reveal style={{ '--d': 1 } as React.CSSProperties}>
              The hard part is not the writing. It is the empty document: a whole world in your head,
              a cursor blinking in front of you, and nothing in between to put your hand on.
            </p>
            <p data-reveal style={{ '--d': 2 } as React.CSSProperties}>
              The people who teach this craft well tend to agree on the thing that helps. Brandon
              Sanderson&rsquo;s lectures treat structure as something you can be taught rather than
              something you are born with. R.F. Kuang talks about the research and the revision as
              the actual work, not the glamorous part. Both point the same way: a story has a shape,
              the shape can be learned, and most of writing is the unglamorous business of keeping
              track of it.
            </p>
            <p data-reveal style={{ '--d': 3 } as React.CSSProperties}>
              So Quil holds the shape for you. Somewhere to put an idea at midnight before it goes.
              A plan that grows as the story does, instead of a document you stop updating in week
              three. And a page to draft on that already knows which chapter it is, which scene you
              are in, and which beat of the story that scene was meant to carry.
            </p>
            <p data-reveal style={{ '--d': 4 } as React.CSSProperties}>
              It is built for the person who has never finished one before, and it is quiet enough
              to stay out of the way of someone who has.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ 05 · STATEMENT + COUNTED STATS ═════════════════════════════════════════════
          The one place the type ration allows Jagot on numerals: display size, and Quil's own
          words rather than the writer's (DESIGN.md §3, "the type ration"). */}
      <section id="shape" className={`${styles.section} ${styles.sunk} ${styles.statement}`}>
        <div className={styles.wrap}>
          <h2 className={styles.statementTitle} data-reveal>
            The plan is not a document
            <Stop />
          </h2>
          <p className={styles.sub} data-reveal style={{ '--d': 1 } as React.CSSProperties}>
            Four story structures ship with the app: Save the Cat, Kish&#333;tenketsu, the Story
            Circle and the Fichtean Curve. Alongside them are eight trope frameworks, each carrying
            its own beats and a plain statement of what a reader is promised by it. You attach a
            beat to a scene, the scene sits in a chapter, and the chapter knows where it falls in
            the whole.
          </p>
          <div className={styles.stats}>
            <div className={styles.stat} data-reveal style={{ '--d': 2 } as React.CSSProperties}>
              <span className={styles.statNum}>
                <Counted value={4} />
              </span>
              <p>Story structures, built in. Save the Cat brings its fifteen beats with it.</p>
            </div>
            <div className={styles.stat} data-reveal style={{ '--d': 3 } as React.CSSProperties}>
              <span className={styles.statNum}>
                <Counted value={8} />
              </span>
              <p>Trope frameworks, from Slow Burn to Found Family, each with its own beats.</p>
            </div>
            <div className={styles.stat} data-reveal style={{ '--d': 4 } as React.CSSProperties}>
              <span className={styles.statNum}>
                <Counted value={95} />
              </span>
              <p>Named beats between them, each one able to carry a scene of yours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 07 · CAPTIONED RAIL · manual, no clock ═════════════════════════════════════ */}
      <section id="surfaces" className={styles.section}>
        <div className={styles.wrap}>
          <h2 className={styles.title} data-reveal>
            Seven surfaces. One draft
            <Stop />
          </h2>
          <p className={styles.sub} data-reveal style={{ '--d': 1 } as React.CSSProperties}>
            Planning, drafting, proofing and finishing are the same document seen from four angles,
            not four apps kept in step by hand.
          </p>
        </div>
        <div className={styles.railBox}>
          {/* NOT looped, unlike the kit's own 07. Looping parks the rail on the middle set, and
              at desktop widths `--edge` is wide enough that the previous clone lands INSIDE the
              left gutter: a third of a card hanging off the screen edge, which is the thing
              REVIEW.md's fix 1 was about. The loop exists so an AUTOPLAYING rail never dead-ends;
              this one is manual with arrows, so it has nothing to protect. */}
          <Rail ariaLabel="Surfaces" transport="arrows">
            {SURFACES.map((s) => (
              <div className="slide" key={s.kicker}>
                <article className={styles.card}>
                  <Label>{s.kicker}</Label>
                  <h3 className={styles.cardTitle}>{s.title}</h3>
                  <p className={styles.cardBody}>{s.body}</p>
                </article>
              </div>
            ))}
          </Rail>
        </div>
      </section>

      {/* ═══ 10 · EXPANDING FEATURE GRID ════════════════════════════════════════════════ */}
      <section id="detail" className={`${styles.section} ${styles.sunk}`}>
        <div className={styles.wrap}>
          <h2 className={styles.title} data-reveal>
            The parts that matter on day two hundred
            <Stop />
          </h2>
          <p className={styles.sub} data-reveal style={{ '--d': 1 } as React.CSSProperties}>
            None of this shows up in a demo. All of it shows up in a long book written across two
            machines over a year.
          </p>
          <div className={styles.grid}>
            <ExpandCard title="Two devices, one chapter" delay={2}>
              <p>
                Prose is uploaded with a conflict check against the copy in the cloud, not blindly.
                When two devices have both moved on, you are shown both and you choose. Planning
                data merges; prose never guesses.
              </p>
            </ExpandCard>
            <ExpandCard title="Offline is the normal case" delay={3}>
              <p>
                Every keystroke is written locally first and queued for OneDrive after. A device
                that has signed in once keeps working with no network at all, and the queue drains
                when the network comes back.
              </p>
            </ExpandCard>
            <ExpandCard title="Three themes, and OLED means black" delay={4}>
              <p>
                Light is a cream specimen sheet. Dark puts a real cream page inside a dark room.
                OLED is true black, but never colourless: your chosen hue still drives its surfaces
                and its keyline.
              </p>
            </ExpandCard>
            <ExpandCard title="Contrast is tested, not eyeballed" delay={5}>
              <p>
                A hundred contrast cases run on every build, checking every ink against every
                ground in all three themes rather than against the page background alone. The first
                pass of this palette still failed five pairings; that is what the gate is for.
              </p>
            </ExpandCard>
            <ExpandCard title="Your own reading font" delay={6}>
              <p>
                Drop a font file into the Quil folder in your OneDrive and it turns up as an option
                for the prose face on every device you write on.
              </p>
            </ExpandCard>
            <ExpandCard title="A draft you can throw away safely" delay={7}>
              <p>
                Deletions go to a recovery shelf for thirty days before they go anywhere else, and
                chapter versions are kept, so a paragraph you cut at midnight is still findable.
              </p>
            </ExpandCard>
          </div>
        </div>
      </section>

      {/* ═══ UNDER THE HOOD · this is a portfolio piece, so the build is part of the work ═ */}
      <section id="stack" className={styles.section}>
        <div className={styles.wrap}>
          <h2 className={styles.title} data-reveal>
            Under the hood
            <Stop />
          </h2>
          <p className={styles.sub} data-reveal style={{ '--d': 1 } as React.CSSProperties}>
            Quil is a personal project, written from nothing. If you are here to look at how it is
            put together rather than to write in it, this part is for you.
          </p>
          <dl className={styles.stack}>
            {STACK.map((row, i) => (
              <div
                key={row.term}
                className={styles.stackRow}
                data-reveal
                style={{ '--d': i + 2 } as React.CSSProperties}
              >
                <dt>{row.term}</dt>
                <dd>{row.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ═══ 14 · FOOTER ═══════════════════════════════════════════════════════════════ */}
      <footer className={`${styles.footer} ${styles.sunk}`}>
        <div className={styles.wrap}>
          <div className={styles.close}>
            <h2 className={styles.closeTitle} data-reveal>
              Nothing is written yet
              <Stop />
            </h2>
            <p className={styles.sub} data-reveal style={{ '--d': 1 } as React.CSSProperties}>
              That is what the library says the first time you open it. It is a fact, not an
              instruction.
            </p>
            <div className={styles.ctas}>{cta('primary')}</div>
          </div>

          <ol className={styles.footnotes}>
            <li>
              <b>1.</b> Quil is a personal project built by one person. There is no company behind
              it, no pricing page, and nothing to sign up for beyond the Microsoft account you
              already have.
            </li>
            <li>
              <b>2.</b> Every structure, trope, beat and check named on this page is in the app now.
              The writing surface at the top is the app&rsquo;s own, drawn in its own tokens, and
              its three proofing flags are ones the real checks raise on that paragraph.
            </li>
            <li>
              <b>3.</b> There is no photography on this page and no second typeface loaded for it.
              Every mark here is text or CSS.
            </li>
          </ol>

          <div className={styles.base}>
            <QMark className={styles.baseMark} />
            <span className={styles.baseNote}>
              Your writing, in your OneDrive, in files you can still open without this app.
            </span>
          </div>
        </div>
      </footer>
    </main>
  )
}
